import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const MACRO_TARGETS = {
  protein: { pct: 30, color: '#10B981', label: 'Protein', icon: 'Dumbbell' },
  carbs:   { pct: 45, color: '#3B82F6', label: 'Carbs',   icon: 'Wheat' },
  fat:     { pct: 25, color: '#F59E0B', label: 'Fat',     icon: 'Droplets' }
};

const CalorieComparisonChart = ({
  caloriesConsumed = 0,
  caloriesBurned = 0,
  dailyCalorieGoal = 2000,
  protein = 0,
  carbs = 0,
  fat = 0
}) => {
  const net = caloriesConsumed - caloriesBurned;
  const netLabel = net >= 0 ? `+${net}` : `${net}`;
  const netColor = net > 200 ? 'text-warning' : net < -200 ? 'text-destructive' : 'text-success';

  const barData = [
    { name: 'Burned', value: caloriesBurned, fill: '#10B981' },
    { name: 'Logged', value: caloriesConsumed, fill: '#5DB075' },
    { name: 'Goal',   value: dailyCalorieGoal, fill: '#94A3B8' }
  ];

  const totalMacroKcal = protein * 4 + carbs * 4 + fat * 9;

  const macroIndicators = useMemo(() => {
    return Object.entries(MACRO_TARGETS)?.map(([key, meta]) => {
      const grams = key === 'protein' ? protein : key === 'carbs' ? carbs : fat;
      const kcalPerGram = key === 'fat' ? 9 : 4;
      const actualKcal = grams * kcalPerGram;
      const actualPct = totalMacroKcal > 0 ? Math.round((actualKcal / totalMacroKcal) * 100) : 0;
      const targetPct = meta.pct;
      const targetGrams = Math.round((dailyCalorieGoal * (targetPct / 100)) / kcalPerGram);
      const diff = grams - targetGrams;
      const status = Math.abs(diff) <= targetGrams * 0.1 ? 'on-track' : diff > 0 ? 'over' : 'under';

      return {
        key,
        label: meta.label,
        color: meta.color,
        icon: meta.icon,
        grams: Math.round(grams),
        targetGrams,
        actualPct,
        targetPct,
        status,
        diff: Math.round(diff),
        fillPct: Math.min(100, targetGrams > 0 ? Math.round((grams / targetGrams) * 100) : 0)
      };
    });
  }, [protein, carbs, fat, dailyCalorieGoal, totalMacroKcal]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      return (
        <div className="bg-card border border-border/40 rounded-lg p-3 shadow-wellness elevation-2">
          <p className="font-medium text-foreground text-sm">{label}</p>
          <p className="text-sm text-primary font-semibold">{payload?.[0]?.value} kcal</p>
        </div>
      );
    }
    return null;
  };

  const statusBadge = (status) => {
    if (status === 'on-track') return { label: 'On Track', cls: 'bg-success/10 text-success' };
    if (status === 'over')     return { label: 'Over',     cls: 'bg-warning/10 text-warning' };
    return                            { label: 'Under',    cls: 'bg-destructive/10 text-destructive' };
  };

  return (
    <motion.div
      className="bg-card rounded-xl p-6 elevation-2"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
            <Icon name="BarChart2" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Calorie Balance</h3>
            <p className="text-xs text-muted-foreground">Burned vs. Logged · Today</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground mb-0.5">Net Balance</p>
          <p className={`text-xl font-bold ${netColor}`}>{netLabel} kcal</p>
        </div>
      </div>
      {/* Side-by-side layout */}
      <div className="grid md:grid-cols-2 gap-6">

        {/* Left: Bar Chart */}
        <div>
          <p className="text-sm font-medium text-muted-foreground mb-3">Daily Overview</p>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.4} />
                <XAxis dataKey="name" stroke="var(--muted-foreground)" fontSize={12} tickLine={false} />
                <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'var(--muted)', opacity: 0.15 }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={56}>
                  {barData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-4 mt-2">
            {barData?.map(b => (
              <div key={b?.name} className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: b?.fill }} />
                <span className="text-xs text-muted-foreground">{b?.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Macro Balance Indicators */}
        <div>
          <p className="text-sm font-medium text-muted-foreground mb-3">Macronutrient Balance</p>
          <div className="space-y-4">
            {macroIndicators?.map((macro) => {
              const badge = statusBadge(macro?.status);
              return (
                <div key={macro?.key} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: macro?.color }}
                      />
                      <span className="text-sm font-medium text-foreground">{macro?.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {macro?.grams}g / {macro?.targetGrams}g
                      </span>
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${badge?.cls}`}>
                        {badge?.label}
                      </span>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="relative h-2 bg-muted/40 rounded-full overflow-hidden">
                    <motion.div
                      className="absolute inset-y-0 left-0 rounded-full"
                      style={{ backgroundColor: macro?.color }}
                      initial={{ width: 0 }}
                      animate={{ width: `${macro?.fillPct}%` }}
                      transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
                    />
                    {/* Target marker at 100% */}
                    <div
                      className="absolute top-0 bottom-0 w-0.5 bg-foreground/30"
                      style={{ left: '100%', transform: 'translateX(-1px)' }}
                    />
                  </div>
                  {/* Actual vs target pct */}
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{macro?.actualPct}% of calories</span>
                    <span>Target: {macro?.targetPct}%</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Calorie balance note */}
          <div className="mt-4 p-3 bg-muted/20 rounded-lg border border-border/30">
            <div className="flex items-start gap-2">
              <Icon
                name={net > 200 ? 'TrendingUp' : net < -200 ? 'TrendingDown' : 'CheckCircle2'}
                size={14}
                className={`mt-0.5 flex-shrink-0 ${netColor}`}
              />
              <p className="text-xs text-muted-foreground leading-relaxed">
                {net > 200
                  ? `You're ${net} kcal above your burn rate today. Consider a light activity session.`
                  : net < -200
                  ? `You're ${Math.abs(net)} kcal below your burn rate. Make sure you're eating enough.`
                  : 'Great balance! Your intake and burn are well aligned today.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CalorieComparisonChart;
